import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, TextField, MenuItem, CircularProgress } from "@mui/material";
import REVIEWABLE_MODULES from "./reviewableModules";
import "../IncidentReports/DailySituationReport.css";

const backendUrl = (process.env.REACT_APP_BACKEND_API_URL_LOCAL || "").replace(/\/$/, "");
const HISTORY_URL = `${backendUrl}/common/report-history/`;

const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const ACTION_OPTIONS = [
    { value: "", label: "All actions" },
    { value: "submitted", label: "Submitted" },
    { value: "viewed", label: "Viewed" },
    { value: "verified", label: "Verified" },
    { value: "rejected", label: "Rejected" },
    { value: "merged", label: "Merged" },
    { value: "unmerged", label: "Unmerged" },
    { value: "reopened", label: "Reopened" },
];

const ACTION_PILL = {
    submitted: { bg: "#e5e7f0", fg: "#52618a" },
    viewed: { bg: "#e2e8f0", fg: "#475569" },
    verified: { bg: "#e2efe3", fg: "#3d7a4c" },
    rejected: { bg: "#f5e2e1", fg: "#ad4141" },
    merged: { bg: "#f4ebd8", fg: "#a8791c" },
    unmerged: { bg: "#fde8d7", fg: "#b95e28" },
    reopened: { bg: "#e0e7ff", fg: "#4338ca" },
};

const hazardLabel = (value) => REVIEWABLE_MODULES.find((m) => m.key === value)?.label || value;

const ReportHistoryLog = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hazardFilter, setHazardFilter] = useState("");
    const [actionFilter, setActionFilter] = useState("");

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (hazardFilter) params.set("hazard_module", hazardFilter);
            if (actionFilter) params.set("action", actionFilter);
            const response = await fetch(`${HISTORY_URL}?${params.toString()}`, { headers: authHeaders() });
            const data = await response.json();
            setEntries(data.history || []);
        } catch (error) {
            console.error("Error fetching report history:", error);
        } finally {
            setLoading(false);
        }
    }, [hazardFilter, actionFilter]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    const sorted = useMemo(
        () => [...entries].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        [entries]
    );

    return (
        <Box className="dsr-container">
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: "18px", mb: 2 }}>Report History</Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField label="Hazard Module" select size="small" value={hazardFilter} onChange={(e) => setHazardFilter(e.target.value)} sx={{ minWidth: 200 }}>
                        <MenuItem value="">All modules</MenuItem>
                        {REVIEWABLE_MODULES.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
                    </TextField>
                    <TextField label="Action" select size="small" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} sx={{ minWidth: 200 }}>
                        {ACTION_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </TextField>
                </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <Box className="dsr-table-scroll">
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>
                    ) : (
                        <table className="dsr-table">
                            <thead>
                                <tr>
                                    <th>When</th><th>Action</th><th>Report</th><th>Hazard</th><th>Actor</th><th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((entry) => {
                                    const pill = ACTION_PILL[entry.action] || { bg: "#e2e8f0", fg: "#475569" };
                                    return (
                                        <tr key={entry.id}>
                                            <td>{new Date(entry.created_at).toLocaleString()}</td>
                                            <td>
                                                <span style={{
                                                    fontFamily: "monospace", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                                                    padding: "2px 8px", borderRadius: 999, background: pill.bg, color: pill.fg,
                                                }}>
                                                    {entry.action}
                                                </span>
                                            </td>
                                            <td>#{entry.object_id}</td>
                                            <td>{hazardLabel(entry.hazard_module)}</td>
                                            <td>{entry.actor_name || "—"}</td>
                                            <td>{entry.notes || ""}</td>
                                        </tr>
                                    );
                                })}
                                {sorted.length === 0 && (
                                    <tr><td colSpan={6} className="dsr-empty">No history entries match the current filter.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ReportHistoryLog;
